
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import '../styles/DcpTool.css';
import { languageOptions, territoryOptions, commonTerritoryOptions } from './DcpToolData';

// --- Configuration Constants ---
const config = {
  defaults: {
    filmTitle: 'FILM',
    contentType: 'SHR',
    versionNumber: '',
    contentModifier: '',
    tempVersion: false,
    prerelease: false,
    HDR: false,
    stereoscopy: '2D',
    frameRate: '24',
    aspectRatio: 'S',
    language: 'EN',
    subtitle: '',
    captionType: '',
    territory: '',
    rating: '',
    audioType: '51',
    resolution: '2K',
    studio: 'BFM',
    facility: '',
    creationDate: () => new Date().toISOString().split('T')[0],
    dcpStandard: 'SMPTE',
    packageType: 'OV'
  },
  separators: {
    primary: '_',
    secondary: '-'
  },
  cssClasses: {
    title: 'dcp-segment-title', content: 'dcp-segment-content', tech: 'dcp-segment-tech',
    lang: 'dcp-segment-lang', audiores: 'dcp-segment-audiores', meta: 'dcp-segment-meta',
    package: 'dcp-segment-package', separator: 'dcp-separator', segmentBase: 'dcp-segment'
  },
  noSubtitleValue: '',
  noLanguageValue: 'XX'
};

// --- Helper Functions ---
function sanitize(value: string, type = 'alphanumHyphenUpper', maxLength = 0) {
  let sanitized = (value || '').trim();
  switch (type) {
    case 'alphanumHyphenUpper':
      sanitized = sanitized.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      break;
    case 'alphanumUpper':
      sanitized = sanitized.toUpperCase().replace(/[^A-Z0-9]/g, '');
      break;
    case 'numeric':
      sanitized = sanitized.replace(/[^0-9]/g, '');
      break;
    case 'date': // Expects YYYY-MM-DD -> YYYYMMDD
      sanitized = sanitized.replace(/-/g, '');
      break;
    case 'langCode':
      sanitized = sanitized.toUpperCase().replace(/[^A-Z]/g, '');
      if (sanitized.length > 3) sanitized = sanitized.slice(0, 3);
      break;
    case 'territoryRating':
      sanitized = sanitized.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      break;
    default:
      break;
  }
  if (maxLength > 0 && sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength);
  }
  return sanitized;
}

interface DcpSegment {
  value: string;
  label: string;
  cssClass: string;
  isOptional?: boolean;
  separator?: string;
  effectiveSeparator?: string;
}

export const DCPToolPage: React.FC = () => {
  // --- State ---
  // Form State
  const [filmTitle, setFilmTitle] = useState(config.defaults.filmTitle);
  const [contentType, setContentType] = useState(config.defaults.contentType);
  const [versionNumber, setVersionNumber] = useState(config.defaults.versionNumber);
  const [contentModifier, setContentModifier] = useState(config.defaults.contentModifier);
  const [tempVersion, setTempVersion] = useState(config.defaults.tempVersion);
  const [prerelease, setPrerelease] = useState(config.defaults.prerelease);
  const [HDR, setHDR] = useState(config.defaults.HDR);
  const [stereoscopy, setStereoscopy] = useState(config.defaults.stereoscopy);
  const [frameRate, setFrameRate] = useState(config.defaults.frameRate);
  const [aspectRatio, setAspectRatio] = useState(config.defaults.aspectRatio);
  const [language, setLanguage] = useState(config.defaults.language);
  const [subtitle, setSubtitle] = useState(config.defaults.subtitle);
  const [captionType, setCaptionType] = useState(config.defaults.captionType);
  const [territory, setTerritory] = useState(config.defaults.territory);
  const [rating, setRating] = useState(config.defaults.rating);
  const [audioType, setAudioType] = useState(config.defaults.audioType);
  const [resolution, setResolution] = useState(config.defaults.resolution);
  const [studio, setStudio] = useState(config.defaults.studio);
  const [facility, setFacility] = useState(config.defaults.facility);
  const [creationDate, setCreationDate] = useState(config.defaults.creationDate());
  const [dcpStandard, setDcpStandard] = useState(config.defaults.dcpStandard);
  const [packageType, setPackageType] = useState(config.defaults.packageType);

  // UI State
  const [titleSegments, setTitleSegments] = useState<DcpSegment[]>([]);
  const [copyStatusVisible, setCopyStatusVisible] = useState(false);
  const [copyButtonDisabled, setCopyButtonDisabled] = useState(false);
  // Dark mode handled by parent or local logic? implementing local logic similar to original for safety
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark as per CSS

  // Refs for non-state relying operations if needed, but here simpler
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Logic ---
  useEffect(() => {
    // Recover dark mode preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') setIsDarkMode(false);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode); // Apply global class for external tool consistency if this runs inside a bigger app, checking context might be safer but parity first
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all fields to their default values?")) {
      setFilmTitle(config.defaults.filmTitle);
      setContentType(config.defaults.contentType);
      setVersionNumber(config.defaults.versionNumber);
      setContentModifier(config.defaults.contentModifier);
      setTempVersion(config.defaults.tempVersion);
      setPrerelease(config.defaults.prerelease);
      setHDR(config.defaults.HDR);
      setStereoscopy(config.defaults.stereoscopy);
      setFrameRate(config.defaults.frameRate);
      setAspectRatio(config.defaults.aspectRatio);
      setLanguage(config.defaults.language);
      setSubtitle(config.defaults.subtitle);
      setCaptionType(config.defaults.captionType);
      setTerritory(config.defaults.territory);
      setRating(config.defaults.rating);
      setAudioType(config.defaults.audioType);
      setResolution(config.defaults.resolution);
      setStudio(config.defaults.studio);
      setFacility(config.defaults.facility);
      setCreationDate(config.defaults.creationDate());
      setDcpStandard(config.defaults.dcpStandard);
      setPackageType(config.defaults.packageType);
    }
  };

  // Calculate derived data for title generation
  useEffect(() => {
    const data = {
      filmTitle: sanitize(filmTitle, 'alphanumHyphenUpper', 25) || config.defaults.filmTitle,
      contentType: contentType || 'XXX',
      versionNumber: sanitize(versionNumber, 'alphanumHyphenUpper'),
      contentModifier: sanitize(contentModifier, 'alphanumHyphenUpper'),
      tempVersion,
      prerelease,
      HDR,
      stereoscopy: stereoscopy || 'XX',
      frameRate: sanitize(frameRate, 'numeric') || 'XX',
      aspectRatio: aspectRatio || 'X',
      language: language || config.noLanguageValue,
      subtitle,
      captionType,
      territory: sanitize(territory, 'territoryRating'),
      rating: sanitize(rating, 'territoryRating'),
      audioType: audioType || 'XX',
      resolution: resolution || 'XX',
      studio: sanitize(studio, 'alphanumUpper', 4),
      facility: sanitize(facility, 'alphanumUpper', 8),
      creationDate: sanitize(creationDate, 'date') || 'YYYYMMDD',
      dcpStandard,
      packageType: packageType || 'XX'
    };

    const contentSegments: DcpSegment[] = [
      { value: data.contentType, label: 'Content Type', cssClass: config.cssClasses.content, separator: config.separators.primary },
      { value: data.versionNumber, label: 'Version', cssClass: config.cssClasses.content, isOptional: true, separator: config.separators.secondary },
      { value: data.contentModifier, label: 'Modifier', cssClass: config.cssClasses.content, isOptional: true, separator: config.separators.secondary },
    ];
    if (data.tempVersion) {
      contentSegments.push({ value: 'Temp', label: 'Temp Version', cssClass: config.cssClasses.content, isOptional: true, separator: config.separators.secondary });
    }
    if (data.prerelease) {
      contentSegments.push({ value: 'Pre', label: 'Pre-release', cssClass: config.cssClasses.content, isOptional: true, separator: config.separators.secondary });
    }
    if (data.HDR) {
      contentSegments.push({ value: 'HDR', label: 'HDR', cssClass: config.cssClasses.content, isOptional: true, separator: config.separators.secondary });
    }

    const segments: DcpSegment[] = [
      { value: data.filmTitle, label: 'Film Title', cssClass: config.cssClasses.title },
      ...contentSegments.filter(seg => !seg.isOptional || seg.value),
      { value: data.stereoscopy, label: 'Stereoscopy', cssClass: config.cssClasses.tech, separator: config.separators.primary },
      { value: data.frameRate, label: 'Frame Rate', cssClass: config.cssClasses.tech, separator: config.separators.secondary },
      { value: data.aspectRatio, label: 'Aspect Ratio', cssClass: config.cssClasses.tech, separator: config.separators.primary },
      { value: data.language, label: 'Language', cssClass: config.cssClasses.lang },
      { value: data.subtitle, label: 'Subtitle', cssClass: config.cssClasses.lang, isOptional: true, separator: config.separators.secondary },
      { value: data.captionType, label: 'Caption Type', cssClass: config.cssClasses.lang, isOptional: true, separator: config.separators.secondary },
      { value: data.territory, label: 'Territory', cssClass: config.cssClasses.lang, isOptional: true, separator: config.separators.primary },
      { value: data.rating, label: 'Rating', cssClass: config.cssClasses.lang, isOptional: true, separator: config.separators.secondary },
      { value: data.audioType, label: 'Audio Type', cssClass: config.cssClasses.audiores, separator: config.separators.primary },
      { value: data.resolution, label: 'Resolution', cssClass: config.cssClasses.audiores, separator: config.separators.primary },
      { value: data.studio, label: 'Studio', cssClass: config.cssClasses.meta, isOptional: true },
      { value: data.facility, label: 'Facility', cssClass: config.cssClasses.meta, isOptional: true, separator: config.separators.secondary },
      { value: data.creationDate, label: 'Creation Date', cssClass: config.cssClasses.meta, separator: config.separators.primary },
      { value: data.dcpStandard, label: 'Standard', cssClass: config.cssClasses.meta, isOptional: true, separator: config.separators.secondary },
      { value: data.packageType, label: 'Package Type', cssClass: config.cssClasses.package, separator: config.separators.primary },
    ];

    const visibleSegments = segments.filter(seg => !seg.isOptional || (seg.isOptional && seg.value));

    visibleSegments.forEach((seg, index) => {
      if (index === 0) {
        seg.effectiveSeparator = '';
        return;
      }

      const precedingVisibleSeg = visibleSegments[index - 1];
      const precedingLabel = precedingVisibleSeg ? precedingVisibleSeg.label : null;
      const currentLabel = seg.label;

      seg.effectiveSeparator = seg.separator !== undefined ? seg.separator : config.separators.primary;

      // Logic Porting from index.html (Lines 1630+)
      if (currentLabel === 'Content Type' && precedingLabel === 'Film Title') {
        seg.effectiveSeparator = config.separators.primary;
      } else if (
        ['Version', 'Modifier', 'Temp Version', 'Pre-release', 'HDR'].includes(currentLabel) &&
        ['Content Type', 'Version', 'Modifier', 'Temp Version', 'Pre-release'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (
        currentLabel === 'Stereoscopy' &&
        ['Content Type', 'Version', 'Modifier', 'Temp Version', 'Pre-release', 'HDR'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Frame Rate' && precedingLabel === 'Stereoscopy') {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (currentLabel === 'Aspect Ratio' && precedingLabel === 'Frame Rate') {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Language' && precedingLabel === 'Aspect Ratio') {
        seg.effectiveSeparator = config.separators.primary;
      } else if (
        (currentLabel === 'Subtitle' && precedingLabel === 'Language') ||
        (currentLabel === 'Caption Type' && ['Language', 'Subtitle'].includes(precedingLabel || ''))
      ) {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (
        currentLabel === 'Territory' &&
        ['Language', 'Subtitle', 'Caption Type'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Rating' && precedingLabel === 'Territory') {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (
        currentLabel === 'Audio Type' &&
        ['Language', 'Subtitle', 'Caption Type', 'Territory', 'Rating'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Resolution' && precedingLabel === 'Audio Type') {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Studio' && precedingLabel === 'Resolution') {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Facility' && precedingLabel === 'Studio') {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (
        currentLabel === 'Creation Date' &&
        ['Resolution', 'Studio', 'Facility'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.primary;
      } else if (currentLabel === 'Standard' && precedingLabel === 'Creation Date') {
        seg.effectiveSeparator = config.separators.secondary;
      } else if (
        currentLabel === 'Package Type' &&
        ['Creation Date', 'Standard'].includes(precedingLabel || '')
      ) {
        seg.effectiveSeparator = config.separators.primary;
      }
    });

    setTitleSegments(visibleSegments);

    // Validation Check Logic
    const hasEssentialData = filmTitle && filmTitle !== config.defaults.filmTitle &&
      contentType && contentType !== 'XXX' &&
      creationDate && creationDate !== 'YYYYMMDD' &&
      frameRate && frameRate !== 'XX';
    // Note: Assuming HTML5 'required' validation is handled visually by styling, 
    // but programmatically for the button we use this check.
    setCopyButtonDisabled(!hasEssentialData);

  }, [filmTitle, contentType, versionNumber, contentModifier, tempVersion, prerelease, HDR, stereoscopy,
    frameRate, aspectRatio, language, subtitle, captionType, territory, rating, audioType, resolution,
    studio, facility, creationDate, dcpStandard, packageType]);


  const handleCopy = () => {
    if (copyButtonDisabled) return;
    const text = titleSegments.map((seg, i) => (i > 0 && seg.effectiveSeparator ? seg.effectiveSeparator : '') + seg.value).join('');

    navigator.clipboard.writeText(text).then(() => {
      showCopyFeedback();
    }).catch(err => {
      console.error('Async Clipboard API failed: ', err);
      // Fallback not strictly necessary in modern React envs usually, but could be added if needed
      alert('Copy failed: ' + text);
    });
  };

  const showCopyFeedback = () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    setCopyStatusVisible(true);
    setCopyButtonDisabled(true);
    copyTimeoutRef.current = setTimeout(() => {
      setCopyStatusVisible(false);
      setCopyButtonDisabled(false); // Re-enable based on validation?? Actually dependent on loop
    }, 2000);
  };

  return (
    <div className="dcp-tool-container" data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="container-inner">
        {/* Themed Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
            DCP Tool
          </h1>
          <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Professional DCP name generator for cinema professionals. 
            Create compliant Digital Cinema Package names following industry standards.
          </p>
          <div className="mt-4 text-sm text-sky-200/70">
            <p>Generate SMPTE-compliant DCP names • Automatic formatting • Festival-ready output</p>
          </div>
        </motion.div>

        <header className="header-container">
        </header>

        <hr />

        <main>
          <section className="title-area" aria-labelledby="generated-title-heading">
            <h2 id="generated-title-heading" className="visually-hidden">Generated DCP Title</h2>
            <div id="generated-title" className="generated-title-container" aria-live="polite">
              {titleSegments.length > 0 ? (
                titleSegments.map((seg, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && seg.effectiveSeparator && (
                      <span className={config.cssClasses.separator}>{seg.effectiveSeparator}</span>
                    )}
                    <span role="text" className={`${config.cssClasses.segmentBase} ${seg.cssClass}`} title={seg.label}>
                      {seg.value}
                    </span>
                  </React.Fragment>
                ))
              ) : 'Enter required fields...'}
            </div>
            <div className="button-group">
              <button
                id="copyButton"
                className="cinematic-button copy-button"
                onClick={handleCopy}
                disabled={copyButtonDisabled || copyStatusVisible}
              >
                Copy Generated Title
              </button>
              <div className="utility-buttons">
                <button className="cinematic-button reset-button" title="Reset all fields to default values" onClick={handleReset}>Reset Form</button>
                <button id="darkModeToggle" className="cinematic-button" onClick={() => setIsDarkMode(!isDarkMode)}>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
              </div>
            </div>
            <span id="copyStatus" className={copyStatusVisible ? 'visible' : ''}>Copied!</span>
            <div style={{ fontSize: '14px', marginTop: '10px' }}>
              The DCP name above updates automatically based on the form below.
              Hover over segments for details (desktop). Use Copy button or select text.
              <br /><i>Ensure generated name meets specific distributor/festival requirements if applicable.</i>
            </div>

            {/* Title Breakdown Section */}
            <div
              id="title-breakdown-container"
              className="title-breakdown"
              style={{ display: titleSegments.length > 0 ? 'block' : 'none' }}
            >
              <h4>Title Breakdown:</h4>
              <div id="title-breakdown-content">
                {titleSegments.map((seg, i) => (
                  <span key={i} className="breakdown-item">
                    <strong>{seg.label}:</strong>
                    <code className={`segment-highlight ${seg.cssClass}`} title={seg.label}>{seg.value}</code>
                  </span>
                ))}
              </div>
            </div>
          </section>

          <hr />

          <form id="dcpForm" className="form-container" onSubmit={(e) => e.preventDefault()}>
            <fieldset>
              <legend>Content Information</legend>
              <div className="form-group">
                <label htmlFor="filmTitle" className="required" title="Main title. Use A-Z, 0-9, hyphen ONLY. Max 20-25 chars recommended for compatibility.">Film Title</label>
                <input
                  type="text" id="filmTitle" placeholder="e.g., MOTIONPICTURE (A-Z, 0-9, -)"
                  required maxLength={25} pattern="[A-Z0-9-]+"
                  value={filmTitle} onChange={(e) => setFilmTitle(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contentType" className="required" title="Type of content (Feature, Trailer, Short, etc.) Select the closest match.">Content Type</label>
                <select id="contentType" required value={contentType} onChange={(e) => setContentType(e.target.value)}>
                  <option value="" disabled>-- Select Type --</option>
                  <option value="ADV">Advertisement</option>
                  <option value="EPS">Episode</option>
                  <option value="FTR">Feature</option>
                  <option value="POL">Policy Trailer</option>
                  <option value="PRO">Promo</option>
                  <option value="PSA">Public Service Announcement</option>
                  <option value="RTG-F">Rating Tag for Feature</option>
                  <option value="RTG-T">Rating Tag for Trailer</option>
                  <option value="SHR">Short</option>
                  <option value="TSR">Teaser</option>
                  <option value="TST">Test</option>
                  <option value="TRL">Trailer</option>
                  <option value="XSH">Transitional</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="versionNumber" title="Optional version ID (e.g., V2, R1, DC=Director's Cut, FIN=Final). Use A-Z, 0-9, hyphen.">Version Number</label>
                <input
                  type="text" id="versionNumber" placeholder="e.g., V2, DC, FIN (Optional, A-Z, 0-9, -)" pattern="[A-Z0-9-]*"
                  value={versionNumber} onChange={(e) => setVersionNumber(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contentModifier" title="Optional custom tag describing the content version (e.g., FESTIVAL, CLEAN, UNCUT). Use A-Z, 0-9, hyphen. Often handled by Version Number, use if distinct meaning needed.">Content Modifier</label>
                <input
                  type="text" id="contentModifier" placeholder="e.g., FESTIVAL, UNCUT (Optional, A-Z, 0-9, -)" pattern="[A-Z0-9-]*"
                  value={contentModifier} onChange={(e) => setContentModifier(e.target.value)}
                />
              </div>

              {/* Checkboxes */}
              <div className="form-group-checkbox">
                <input type="checkbox" id="tempVersion" checked={tempVersion} onChange={(e) => setTempVersion(e.target.checked)} />
                <label htmlFor="tempVersion" title="Check if this is a temporary version (adds 'Temp' to name)">Temp Version</label>
              </div>
              <div className="form-group-checkbox">
                <input type="checkbox" id="prerelease" checked={prerelease} onChange={(e) => setPrerelease(e.target.checked)} />
                <label htmlFor="prerelease" title="Check if this is a pre-release version (adds 'Pre' to name)">Pre-release</label>
              </div>
              <div className="form-group-checkbox">
                <input type="checkbox" id="HDR" checked={HDR} onChange={(e) => setHDR(e.target.checked)} />
                <label htmlFor="HDR" title="Check if this content includes HDR elements (adds 'HDR' to name)">HDR</label>
              </div>
            </fieldset>

            <fieldset>
              <legend>Technical Specifications</legend>
              <div className="form-group">
                <label htmlFor="stereoscopy" className="required" title="Image dimensionality: 2D (standard) or 3D.">Stereoscopy</label>
                <select id="stereoscopy" required value={stereoscopy} onChange={(e) => setStereoscopy(e.target.value)}>
                  <option value="2D">2D</option>
                  <option value="3D">3D</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="frameRate" className="required" title="Frames per second (e.g., 24, 25, 30, 48, 60, etc.). Use numbers only.">Frame Rate</label>
                <input
                  type="number" id="frameRate" placeholder="e.g., 24" min={1} max={120} step={1} required
                  value={frameRate} onChange={(e) => setFrameRate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="aspectRatio" className="required" title="Projector Aspect Ratio Setting: F=Flat (1.85), S=Scope (2.39), C=Full Container (1.90). Defines masking.">Projector Aspect Ratio</label>
                <select id="aspectRatio" required value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                  <option value="F">Flat (F - 1.85:1)</option>
                  <option value="S">Scope (S - 2.39:1)</option>
                  <option value="C">Full Container (C - 1.90:1)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="audioType" className="required" title="Audio channel configuration or special purpose track type.">Audio Type</label>
                <select id="audioType" required value={audioType} onChange={(e) => setAudioType(e.target.value)}>
                  <option value="" disabled>-- Select Audio Type --</option>
                  <option value="MOS">No Audio (MOS)</option>
                  <option value="10">Mono (1.0)</option>
                  <option value="20">Stereo (2.0)</option>
                  <option value="21">Stereo + Sub (2.1)</option>
                  <option value="51">5.1 Surround</option>
                  <option value="71">7.1 Surround (Common)</option>
                  <option value="71SDDS">7.1 SDDS Layout</option>
                  <option value="IAB">Immersive Audio Bitstream (IAB - SMPTE)</option>
                  <option value="ATMOS">Dolby ATMOS (Often packaged as IAB or specific labels)</option>
                  <option value="AURO">Barco AURO</option>
                  <option value="DTSX">DTS:X</option>
                  <option value="HI">Hearing Impaired Track (HI)</option>
                  <option value="VI">Vision Impaired Track (VI)</option>
                  <option value="SL">Sign Language Video (Video track)</option>
                  <option value="DBOX">D-Box Motion Control</option>
                  <option value="OTHER">Other (Use only if needed)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="resolution" className="required" title="Image Resolution (Container Size): HD (rare), 2K (common), 4K (common).">Resolution</label>
                <select id="resolution" required value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  <option value="HD">HD (1920x1080)</option>
                  <option value="2K">2K (e.g., 2048x1080)</option>
                  <option value="4K">4K (e.g., 4096x2160)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="dcpStandard" title="DCP Standard used: SMPTE (modern standard, recommended) or Interop (IOP - older standard).">DCP Standard</label>
                <select id="dcpStandard" value={dcpStandard} onChange={(e) => setDcpStandard(e.target.value)}>
                  <option value="">-- Not Specified --</option>
                  <option value="SMPTE">SMPTE</option>
                  <option value="IOP">INTEROP (IOP)</option>
                </select>
              </div>
            </fieldset>

            <fieldset>
              <legend>Localization & Rating</legend>
              <div className="form-group">
                <label htmlFor="language" title="Primary audio language (ISO 639-2/T Code, 2-3 letters, uppercase). Use 'XX' for non-verbal content.">Language</label>
                <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="" disabled>-- Select Audio Language --</option>
                  {languageOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subtitle" title="Primary subtitle language (ISO 639-2/T Code, 2-3 letters, uppercase). Leave blank if no subtitles.">Subtitle</label>
                <select id="subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)}>
                  <option value="" disabled>-- Select Subtitle (Optional) --</option>
                  <option value={config.noSubtitleValue}>No Subtitle</option>
                  {languageOptions.filter(l => l.value !== 'XX').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="captionType" title="Type of captions: OCAP (Open/burned-in) or CCAP (Closed/separate track). Select if applicable.">Caption Type</label>
                <select id="captionType" value={captionType} onChange={(e) => setCaptionType(e.target.value)}>
                  <option value="">-- No Captions --</option>
                  <option value="OCAP">Open Captions (OCAP)</option>
                  <option value="CCAP">Closed Captions (CCAP)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="territory" title="Target territory/region code (e.g., US, GB, CA-QC, WW). Optional, use ISO 3166-1 alpha-2 or common codes. Uppercase A-Z, hyphen.">Territory</label>
                <input
                  type="text" id="territory" list="territory-list" placeholder="e.g., US, GB, WW (Optional, A-Z, -)" pattern="[A-Z-]*"
                  value={territory} onChange={(e) => setTerritory(e.target.value)}
                />
                <datalist id="territory-list">
                  {commonTerritoryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.text} ({opt.value})</option>
                  ))}
                  {territoryOptions.sort((a, b) => a.text.localeCompare(b.text)).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.text} ({opt.value})</option>
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label htmlFor="rating" title="Rating system & value (e.g., MPAA-PG, BBFC-15, FSK-12). Use agency prefix if known. Optional. Uppercase A-Z, 0-9, hyphen.">Rating</label>
                <input
                  type="text" id="rating" list="rating-list" placeholder="e.g., PG, R, 15, FSK-12 (Optional, A-Z, 0-9, -)" pattern="[A-Z0-9-]*"
                  value={rating} onChange={(e) => setRating(e.target.value)}
                />
                <datalist id="rating-list">
                  <option value="MPAA-G" /> <option value="MPAA-PG" /> <option value="MPAA-PG13" /> <option value="MPAA-R" /> <option value="MPAA-NC17" />
                  <option value="BBFC-U" /> <option value="BBFC-PG" /> <option value="BBFC-12A" /> <option value="BBFC-15" /> <option value="BBFC-18" />
                  <option value="FSK-0" /> <option value="FSK-6" /> <option value="FSK-12" /> <option value="FSK-16" /> <option value="FSK-18" />
                  <option value="NR" /> <option value="UNRATED" />
                </datalist>
              </div>
            </fieldset>

            <fieldset>
              <legend>Metadata & Package</legend>
              <div className="form-group">
                <label htmlFor="studio" title="Studio Code (assigned by ISDCF or custom 3-4 letter code). Optional. Uppercase A-Z, 0-9.">Studio</label>
                <input
                  type="text" id="studio" list="studio-list" placeholder="e.g., FOX, UNI, BFM (Optional, A-Z, 0-9)" pattern="[A-Z0-9]*" maxLength={4}
                  value={studio} onChange={(e) => setStudio(e.target.value)}
                />
                <datalist id="studio-list">
                  <option value="DIS" /> <option value="FOX" /> <option value="UNI" />
                  <option value="WB" /> <option value="SONY" /> <option value="PAR" />
                  <option value="LGF" /> <option value="BFM" />
                </datalist>
              </div>

              <div className="form-group">
                <label htmlFor="facility" title="Facility/Lab Code (3-8 letter code for the creator). Optional. Uppercase A-Z, 0-9.">Facility</label>
                <input
                  type="text" id="facility" placeholder="e.g., KEM, AVM, FA, CM, DOCMA, etc." pattern="[A-Z0-9]*" maxLength={8}
                  value={facility} onChange={(e) => setFacility(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="creationDate" className="required" title="Date the DCP was created/packaged (YYYY-MM-DD). Used to generate YYYYMMDD part.">Creation Date</label>
                <input
                  type="date" id="creationDate" required
                  value={creationDate} onChange={(e) => setCreationDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="packageType" className="required" title="Package Type: OV (Original Version - complete standalone package) or VF (Version File - requires a specific OV, contains only differences like audio/subs).">Package Type</label>
                <select id="packageType" required value={packageType} onChange={(e) => setPackageType(e.target.value)}>
                  <option value="OV">Original Version (OV)</option>
                  <option value="VF">Version File (VF)</option>
                </select>
              </div>
            </fieldset>

          </form>

        </main>

        <motion.footer 
          className="footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="text-center space-y-4">
            <div className="text-lg font-semibold text-sky-400">
              DCP Name Generator - V1.1.4
            </div>
            
            <div className="text-gray-300 text-sm">
              Compiled by Sangam Panta | Film Supervisor, Cinema Administrator | BFM, Tallinn University, Estonia
            </div>
            
            <div>
              <a 
                href="http://www.sangampanta.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sky-300 hover:text-sky-200 transition-colors duration-200 underline"
              >
                www.sangampanta.com
              </a>
            </div>
            
            <div className="pt-4 border-t border-gray-700">
              <p className="text-gray-400 text-sm leading-relaxed">
                This tool helps generate DCP folder names based on the 
                <span className="text-sky-300 font-medium"> ISDCF Digital Cinema Naming Convention (DCNC)</span>.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Always refer to the official 
                <a 
                  href="https://www.isdcf.com/dcnc/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sky-300 hover:text-sky-200 transition-colors duration-200 underline ml-1"
                >
                  ISDCF DCNC Document
                </a>
                {' '}for full specifications and updates.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Use A-Z, 0-9, and hyphens (-) only in most fields. Keep Film Title concise. Consistency is key.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                Ensure the generated name meets any specific requirements from distributors or festivals.
              </p>
            </div>
          </div>
        </motion.footer>
      </div>
    </div>
  );
}

export default DCPToolPage;
